import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import DossierList from './components/DossierList';
import DossierDetail from './components/DossierDetail';
import NotaryWorkspace from './components/NotaryWorkspace';
import TransactionProgressTracker from './components/TransactionProgressTracker';
import {
  getNotaireProfile,
  getDossiersByNotaire,
  getNotaireDashboardStats,
} from '../../services/notairePortalService';

const StatCard = ({ icon, label, value, accent = 'brand-earth', sub = null }) => (
  <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-4 flex items-start space-x-3">
    <div className={`w-10 h-10 rounded-xl bg-${accent}/10 flex items-center justify-center flex-shrink-0`}>
      <Icon name={icon} size={18} className={`text-${accent}`} />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] text-text-secondary font-system font-body-medium uppercase tracking-wider truncate">{label}</p>
      <p className="text-ios-title3 font-display font-display-semibold text-text-primary mt-0.5 leading-tight">{value}</p>
      {sub && <p className="text-ios-caption1 text-text-secondary mt-0.5">{sub}</p>}
    </div>
  </div>
);

const NotaireDashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [notaireProfile, setNotaireProfile] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('workspace'); // 'workspace' | 'dossiers' | 'detail' | 'tracker'
  const [error, setError] = useState('');

  const loadProfile = useCallback(async () => {
    if (!user?.id) return;
    setProfileLoading(true);
    const { data, error: err } = await getNotaireProfile(user?.id);
    if (err || !data) {
      navigate('/notaire/login', { replace: true });
      return;
    }
    setNotaireProfile(data);
    setProfileLoading(false);
    return data;
  }, [user?.id, navigate]);

  const loadDossiers = useCallback(async (notaryId) => {
    if (!notaryId) return;
    setLoading(true);
    const [dossiersResult, statsResult] = await Promise.all([
      getDossiersByNotaire(notaryId),
      getNotaireDashboardStats(notaryId),
    ]);
    if (!dossiersResult?.error) setDossiers(dossiersResult?.data || []);
    if (!statsResult?.error) setStats(statsResult?.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/notaire/login', { replace: true });
      return;
    }
    loadProfile()?.then(profile => {
      if (profile?.id) loadDossiers(profile?.id);
    });
  }, [user, loadProfile, loadDossiers, navigate]);

  const handleSelectDossier = (dossier) => {
    setSelectedDossier(dossier);
    setActiveTab('detail');
  };

  const handleDossierUpdated = async () => {
    if (notaireProfile?.id) {
      await loadDossiers(notaireProfile?.id);
      if (selectedDossier) {
        const updated = dossiers?.find(d => d?.id === selectedDossier?.id);
        if (updated) setSelectedDossier(updated);
      }
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/notaire/login', { replace: true });
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="w-10 h-10 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
          <p className="text-ios-callout text-text-secondary font-system">Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Espace Notaire – Kapital States</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen bg-surface">
        {/* Top Navigation */}
        <header className="bg-white border-b border-border/50 sticky top-0 z-40 shadow-ios-light">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/homepage" className="flex items-center space-x-2.5 group">
                <div className="w-8 h-8 bg-gradient-to-br from-brand-gold to-brand-earth rounded-xl flex items-center justify-center shadow-ios-light group-hover:scale-105 transition-transform">
                  <Icon name="Building2" size={16} color="white" strokeWidth={2.5} />
                </div>
                <div className="hidden sm:flex flex-col">
                  <span className="font-display font-display-semibold text-ios-callout text-brand-earth leading-tight">Kapital States</span>
                  <span className="text-[10px] text-text-secondary font-system leading-tight">Espace Notaire</span>
                </div>
              </Link>
              <div className="hidden sm:block w-px h-6 bg-border/50" />
              <div className="hidden sm:flex items-center space-x-1.5 px-2.5 py-1 bg-brand-gold/10 border border-brand-gold/30 rounded-full">
                <Icon name="Scale" size={12} className="text-brand-earth" />
                <span className="text-[11px] font-system font-body-semibold text-brand-earth">Portail Notaires</span>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="hidden sm:flex flex-col items-end">
                <p className="text-ios-callout font-system font-body-semibold text-text-primary leading-tight">
                  {notaireProfile?.notary_name || user?.email}
                </p>
                <p className="text-ios-caption1 text-text-secondary font-system">
                  {notaireProfile?.office_address || 'Notaire partenaire'}
                </p>
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-1.5 px-3 py-2 rounded-xl border border-border text-ios-caption1 font-system font-body-medium text-text-secondary hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all">
                <Icon name="LogOut" size={14} />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard icon="FolderOpen" label="Total dossiers" value={stats?.total ?? '—'} />
            <StatCard icon="Clock" label="Nouveaux" value={stats?.nouveau ?? '—'} accent="amber-500" />
            <StatCard icon="CheckCircle" label="Validés" value={stats?.valide ?? '—'} accent="green-600" />
            <StatCard icon="BadgeCheck" label="Badges émis" value={stats?.badges ?? '—'} accent="brand-gold" />
          </div>

          {/* Main navigation tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide mb-6">
            {[
              { id: 'workspace', label: 'Mon Espace', icon: 'LayoutDashboard' },
              { id: 'dossiers', label: 'Dossiers', icon: 'FolderOpen', badge: stats?.nouveau },
              { id: 'detail', label: 'Dossier actif', icon: 'FileText', hidden: !selectedDossier },
              { id: 'tracker', label: 'Suivi transaction', icon: 'GitBranch', hidden: !selectedDossier },
            ]?.filter(t => !t?.hidden)?.map(tab => (
              <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                className={`flex-shrink-0 flex items-center space-x-2 px-4 py-2.5 rounded-xl text-ios-callout font-system font-body-medium transition-all ${
                  activeTab === tab?.id
                    ? 'bg-brand-earth text-white shadow-ios-light'
                    : 'bg-white border border-border/50 text-text-secondary hover:border-brand-gold/40'
                }`}>
                <Icon name={tab?.icon} size={15} />
                <span>{tab?.label}</span>
                {tab?.badge > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-system font-body-semibold ${activeTab === tab?.id ? 'bg-white/20 text-white' : 'bg-red-500 text-white'}`}>
                    {tab?.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'workspace' && (
            <NotaryWorkspace
              notaireProfile={notaireProfile}
              dossiers={dossiers}
              stats={stats}
              onSelectDossier={handleSelectDossier}
            />
          )}

          {activeTab === 'dossiers' && (
            <div className="flex gap-6">
              <div className={`${selectedDossier ? 'hidden lg:block lg:w-2/5' : 'w-full'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-ios-headline font-display font-display-semibold text-text-primary">Mes dossiers</h2>
                  <span className="text-ios-caption1 text-text-secondary font-system bg-white border border-border/50 px-2.5 py-1 rounded-full">
                    {dossiers?.length} dossier{dossiers?.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <DossierList
                  dossiers={dossiers}
                  loading={loading}
                  onSelectDossier={handleSelectDossier}
                  selectedDossierId={selectedDossier?.id}
                />
              </div>
            </div>
          )}

          {activeTab === 'detail' && selectedDossier && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button onClick={() => setActiveTab('dossiers')}
                  className="flex items-center space-x-1.5 text-ios-callout font-system font-body-medium text-text-secondary hover:text-brand-earth transition-colors">
                  <Icon name="ArrowLeft" size={16} />
                  <span>Retour aux dossiers</span>
                </button>
                <button onClick={() => setActiveTab('tracker')}
                  className="flex items-center space-x-1.5 px-3 py-2 bg-brand-gold/10 border border-brand-gold/30 text-brand-earth rounded-xl text-ios-caption1 font-system font-body-semibold hover:bg-brand-gold/20 transition-colors">
                  <Icon name="GitBranch" size={14} />
                  <span>Voir la timeline</span>
                </button>
              </div>
              <DossierDetail
                dossier={selectedDossier}
                onDossierUpdated={handleDossierUpdated}
                notaireProfile={notaireProfile}
              />
            </div>
          )}

          {activeTab === 'tracker' && selectedDossier && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <button onClick={() => setActiveTab('detail')}
                  className="flex items-center space-x-1.5 text-ios-callout font-system font-body-medium text-text-secondary hover:text-brand-earth transition-colors">
                  <Icon name="ArrowLeft" size={16} />
                  <span>Retour au dossier</span>
                </button>
                <div className="flex items-center space-x-2">
                  <span className="text-ios-caption1 font-system font-body-semibold text-brand-earth bg-brand-gold/10 px-2.5 py-1 rounded-full">
                    {selectedDossier?.reference}
                  </span>
                </div>
              </div>
              <TransactionProgressTracker dossier={selectedDossier} isNotaire={true} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotaireDashboard;
